import type {WidgetType} from "constants/WidgetConstants";
import * as React from "react";
import type {WidgetProps, WidgetState} from "widgets/BaseWidget";
import TaroWidget from "widgetsTaro";
import {ValidationTypes} from "constants/WidgetValidation";
import type {Stylesheet} from "entities/AppTheming";
import {DefaultAutocompleteDefinitions} from "widgets/WidgetUtils";
import type {AutocompletionDefinitions} from "widgets/constants";
import TGridWidget from "../component";
import {DerivedPropertiesMap} from "../../../utils/WidgetFactory";
import {EventType} from "../../../constants/AppsmithActionConstants/ActionConstants";

class TGrid extends TaroWidget<TGridWidgetProps, WidgetState> {
  constructor(props: TGridWidgetProps) {
    super(props);
    this.onItemClick = this.onItemClick.bind(this);
  }

  static getAutocompleteDefinitions(): AutocompletionDefinitions {
    return {
      value: "string",
      isVisible: DefaultAutocompleteDefinitions.isVisible,
    };
  }

  static getPropertyPaneContentConfig() {
    return [
      {
        sectionName: "一般",
        children: [
          {
            propertyName: "label",
            label: "组件标签",
            helpText: "设置该组件的标签",
            controlType: "INPUT_TEXT",
            placeholderText: "",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            propertyName: "currentData",
            label: "数据",
            helpText: "Sets the label of the button",
            controlType: "CAROUSEL_ARRAY",
            placeholderText: "确定",
            isBindProperty: true,
            isCarousel: true,
            isDescription: true,
            isTriggerProperty: true,
            isJSConvertible: false,
            validation: {type: ValidationTypes.ARRAY},
            iconSize: '58px',
          },
          {
            propertyName: "currentDataDes",
            label: "描述",
            helpText: "Sets the label of the button",
            toKey: 'currentData',
            controlType: "DES_LIST",
            placeholderText: "确定",
            isBindProperty: true,
            isTriggerProperty: true,
            isJSConvertible: false,
            validation: {type: ValidationTypes.ARRAY},
            dependencies: ['currentData']
          },
          {
            helpText: "",
            propertyName: "columnNum",
            label: "每列个数",
            controlType: "NUMERIC_INPUT",
            isJSConvertible: false,
            isBindProperty: true,
            isTriggerProperty: false,
            min: 1,
            validation: {
              type: ValidationTypes.NUMBER,
              params: {
                natural: true,
              },
            }
          }
        ],
      }
    ];
  }

  static getPropertyPaneStyleConfig() {
    return [
      {
        sectionName: "一般",
        children: [{
          propertyName: "fontSize",
          label: "字体大小",
          helpText: "Control the font size of the label associated",
          controlType: "DROP_DOWN",
          defaultValue: "0.875rem",
          options: [
            {
              label: "S",
              value: "0.875rem",
              subText: "0.875rem",
            },
            {
              label: "M",
              value: "1rem",
              subText: "1rem",
            },
            {
              label: "L",
              value: "1.25rem",
              subText: "1.25rem",
            },
            {
              label: "XL",
              value: "1.875rem",
              subText: "1.875rem",
            },
            {
              label: "XXL",
              value: "3rem",
              subText: "3rem",
            },
            {
              label: "3XL",
              value: "3.75rem",
              subText: "3.75rem",
            },
          ],
          isJSConvertible: true,
          isBindProperty: true,
          isTriggerProperty: false,
          validation: {type: ValidationTypes.TEXT},
        },
          {
            propertyName: "color",
            label: "颜色",
            helpText: "Controls the color of the text displayed",
            controlType: "COLOR_PICKER",
            isJSConvertible: false,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^(?![<|{{]).+/,
              },
            },
          }
        ]
      }
    ]
  }


  static getStylesheetConfig(): Stylesheet {
    return {
      color: "{{appsmith.theme.colors.textColor}}"
    };
  }

  static getMetaPropertiesMap(): Record<string, any> {
    return {
      isDirty: false,
      value: undefined
    };
  }

  static getDerivedPropertiesMap(): DerivedPropertiesMap {
    return {
      getValue: `{{ this.value }}`
    };
  }

  updateWidgetProperty = (propertyValues: string) => {
    if (!this.props.isDirty) {
      this.props.updateWidgetMetaProperty("isDirty", true);
    }
    this.props.updateWidgetMetaProperty("value", propertyValues);
  }
  onItemClick(item:any){
    let actionConfig:any = {
      apiTarget: this.props.widgetId,
      navigateBlank: item.topagetype
    }
    if(item.actionType === 'toPage'){
      actionConfig = {
        ...actionConfig,
        action: 'navigateTo',
        navigatePage: item.topageid,
      }
    }else if(item.actionType === 'toUrl'){
      actionConfig = {
        ...actionConfig,
        action: 'navigateUrl',
        navigateURL: item.toUrl
      }
    }else{
      return ;
    }

    let dynamicString:any = {
      name: 'onItemClick',
      children: [
        {
          name: 'block',
          type: 'block',
          children: [
            {
              name: 'action',
              type: 'action',
              actionConfig
            }
          ]
        }
      ]
    };
    super.executeAction({
      triggerPropertyName: "onClick",
      dynamicString,
      event: {
        type: EventType.ON_CLICK
      },
    });
  }

  getPageView() {
    return (
      <TGridWidget onItemClick={this.onItemClick} {...this.props} updateWidgetProperty={this.updateWidgetProperty}/>
    );
  }


  static getWidgetType(): WidgetType {
    return "AT_GRID_WIDGET";
  }
}

export interface TGridWidgetProps extends WidgetProps {
  value: any[];
  enableDownload?: boolean;
  enableRotation?: boolean;
  onClick?: string;
  borderRadius: string;
  boxShadow?: string;
}

export default TGrid;
